import React, { ReactElement, useState } from 'react';
import { Box, Button, Paper } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { DialogWindow, ComparisonProject } from '..';
import { useProfileId, useStateMap } from '../../../hooks';
import { MapDetailDto } from '../../../models';
import {
	Project,
	AppThunkDispatch,
	MapAction,
	AppState,
	ComparisonAction,
	comparisonLoadEmpty
} from '../../../redux';
import { Body2, ErrorBoundary, Header5 } from '../../atoms';
import { MapSelector } from '../../organisms';

import './create-comparison.styles.scss';
import { useTranslation } from 'react-i18next';

export interface ICreateComparisonProps {
	project: Project;
}

export interface ICreateComparisonStateProps {
	selectedProjects: Project[];
	dispatch: AppThunkDispatch<MapAction>;
	createComparison: (
		project: Project,
		selectedProjects: Project[],
		profileId: string
	) => void;
}

function CreateComparisonView(
	props: ICreateComparisonProps & ICreateComparisonStateProps
): ReactElement {
	const { project, selectedProjects, createComparison, dispatch } = props;
	const { t } = useTranslation(['comparison', 'general']);

	const [selectionDialogOpen, setSelectionDialogOpen] =
		useState<boolean>(false);
	const history = useHistory();
	const profileId = useProfileId();
	const map: MapDetailDto | undefined = useStateMap(
		project.metaDataId,
		dispatch
	);

	function saveComparison(): void {
		if (!map) return;

		createComparison(project, selectedProjects, profileId);
		history.replace('/comparison');
	}

	function returnToMapViewer(): void {
		if (project.id) {
			history.replace(`/project/${project.id}`);
		} else {
			history.replace(`/map/${project.metaDataId}`);
		}
	}

	return (
		<Box className="create-comparison">
			{map !== undefined && (
				<>
					<Box className="create-comparison-container">
						<Box className="create-comparison-map-viewer">
							<ErrorBoundary>
								<ComparisonProject
									key={`1-${project.metaDataId}`}
									hideControls
									index={1}
									project={project}
									canRemove={false}
								/>
							</ErrorBoundary>
						</Box>
						<Box className="select-map-prompt">
							<Header5>{t('comparison:chooseCompare')}</Header5>
							<Body2>{t('comparison:chooseMax4Maps')}</Body2>
							<Button
								variant="contained"
								color="primary"
								onClick={() => setSelectionDialogOpen(true)}
							>
								<AddIcon />
								{t('comparison:chooseMaps')}
							</Button>
						</Box>
					</Box>
					<Paper elevation={3} className="create-comparison-footer">
						<Button
							color="primary"
							variant="outlined"
							onClick={returnToMapViewer}
						>
							<CloseIcon /> {t('general:dialog.cancel')}
						</Button>
						<Button
							onClick={saveComparison}
							variant="contained"
							color="primary"
							disabled={true}
						>
							<AddIcon />
							{t('comparison:addToComparison')}
						</Button>
					</Paper>
					<DialogWindow
						fullScreen={true}
						open={selectionDialogOpen}
						onClose={() => setSelectionDialogOpen(false)}
					>
						<MapSelector
							selectedProjects={selectedProjects}
							onConfirm={saveComparison}
							onCancel={returnToMapViewer}
							project={project}
						/>
					</DialogWindow>
				</>
			)}
		</Box>
	);
}

function mapStateToProps(state: AppState) {
	return {
		selectedProjects: state.mapSelector.selectedProjects
	};
}

function mapDispatchToProps(
	dispatch: AppThunkDispatch<MapAction | ComparisonAction>
) {
	return {
		createComparison: (
			project: Project,
			selectedProjects: Project[],
			profileId: string
		) =>
			dispatch(comparisonLoadEmpty(project, selectedProjects, profileId)),
		dispatch
	};
}

export const CreateComparison = connect(
	mapStateToProps,
	mapDispatchToProps
)(CreateComparisonView);
